import React from 'react';
import { Link } from 'react-router-dom';

const Section = ({ title, children }) => (
  <div style={{ marginBottom: '2.5rem' }}>
    <h2 style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '1.15rem', letterSpacing: '1px', marginBottom: '0.9rem', color: 'var(--text)' }}>{title}</h2>
    <div style={{ color: 'var(--muted)', lineHeight: '1.85', fontSize: '0.9rem' }}>{children}</div>
  </div>
);

export default function Terms() {
  return (
    <div className="page-shell page-stack" style={{ maxWidth: '820px', margin: '0 auto' }}>
      <section className="section-stack">
        <div className="section-tag">Legal</div>
        <h1 className="section-title">Terms of Service</h1>
        <p className="section-copy">
          Last updated: March 2026. Please read these terms carefully before using the CarbonX platform.
        </p>
      </section>

      {/* Non-broker disclaimer banner */}
      <div className="card" style={{ background: 'rgba(255,238,0,0.05)', borderColor: 'rgba(255,238,0,0.25)', marginBottom: '2rem' }}>
        <div className="stat-label" style={{ color: 'var(--neon-yellow)', marginBottom: '0.5rem' }}>Important Disclaimer</div>
        <p style={{ color: '#ffe873', fontSize: '0.88rem', lineHeight: '1.7', margin: 0 }}>
          CarbonX is a <strong>software and information platform</strong>. We are not a SEBI-registered broker, CERC-registered power exchange, investment adviser, or intermediary of any kind. We do not execute, clear, or settle real trades in Carbon Credit Certificates (CCCs) or any other securities. All trading shown on this platform is <strong>simulated for educational purposes only</strong>.
        </p>
      </div>

      <div className="surface-panel" style={{ padding: '2.5rem' }}>
        <Section title="1. Acceptance of Terms">
          <p>By accessing or using the CarbonX platform ("Platform"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, do not use the Platform.</p>
          <p style={{ marginTop: '0.8rem' }}>These Terms govern your use of the Platform operated by CarbonX Technology Private Limited ("Company", "we", "us", or "our"), a company incorporated under the Companies Act, 2013 in India.</p>
        </Section>

        <Section title="2. Nature of the Platform">
          <p>CarbonX is a <strong>software, data, and educational tool</strong> that provides:</p>
          <ul style={{ paddingLeft: '1.5rem', marginTop: '0.6rem', display: 'grid', gap: '0.4rem' }}>
            <li>Information and analysis about India's Carbon Credit Trading Scheme (CCTS) and Indian Carbon Market (ICM)</li>
            <li>Simulated trading interfaces for educational purposes</li>
            <li>A discovery directory for carbon credit project developers and potential buyers</li>
            <li>Market reference data aggregated from publicly available sources</li>
          </ul>
          <p style={{ marginTop: '0.9rem' }}><strong>The Platform does not:</strong></p>
          <ul style={{ paddingLeft: '1.5rem', marginTop: '0.6rem', display: 'grid', gap: '0.4rem' }}>
            <li>Execute or settle real trades in Carbon Credit Certificates (CCCs) or any other securities</li>
            <li>Hold, custody, or transfer real carbon credits or funds on behalf of users</li>
            <li>Act as a regulated exchange, broker, or intermediary under CERC, SEBI, or any other regulatory authority</li>
            <li>Provide investment advice or recommendations</li>
          </ul>
        </Section>

        <Section title="3. Simulated Trading — Educational Use Only">
          <p>Any trading, portfolio, or transaction features on this Platform are <strong>entirely simulated</strong>. Simulated trades:</p>
          <ul style={{ paddingLeft: '1.5rem', marginTop: '0.6rem', display: 'grid', gap: '0.4rem' }}>
            <li>Do not involve real money or real carbon credits</li>
            <li>Do not represent actual market transactions</li>
            <li>Are provided solely for educational understanding of how carbon markets work</li>
            <li>Are not investment advice</li>
          </ul>
          <p style={{ marginTop: '0.9rem' }}>Simulated prices are reference snapshots and may not reflect actual market conditions on any exchange.</p>
        </Section>

        <Section title="4. Project Discovery Directory">
          <p>The project listings on this Platform are self-reported by users. CarbonX does not verify the accuracy, completeness, or legality of any project information. We are not a party to any transaction between project developers and potential buyers. Users must conduct independent due diligence before engaging in any real-world transaction.</p>
          <p style={{ marginTop: '0.8rem' }}>Expressing interest in a project does not create any binding obligation, contract, or commitment for any party.</p>
        </Section>

        <Section title="5. User Accounts and KYC">
          <p>To access certain features, you must create an account and complete our KYC (Know Your Customer) verification. You are responsible for maintaining the confidentiality of your account credentials. You must provide accurate and complete information during registration.</p>
          <p style={{ marginTop: '0.8rem' }}>KYC verification on this Platform is for identity verification within our software system only and does not constitute any regulatory approval for real carbon credit trading.</p>
        </Section>

        <Section title="6. Prohibited Activities">
          <p>You agree not to:</p>
          <ul style={{ paddingLeft: '1.5rem', marginTop: '0.6rem', display: 'grid', gap: '0.4rem' }}>
            <li>Use the Platform for any unlawful purpose</li>
            <li>Provide false or misleading information</li>
            <li>Attempt to manipulate simulated prices or data</li>
            <li>Use automated tools to scrape or misuse Platform data</li>
            <li>Impersonate any person or entity</li>
          </ul>
        </Section>

        <Section title="7. Intellectual Property">
          <p>All content on this Platform, including software, text, graphics, and data, is the property of CarbonX Technology Private Limited or its licensors. You may not reproduce, distribute, or create derivative works without our written permission.</p>
        </Section>

        <Section title="8. Disclaimer of Warranties">
          <p>The Platform is provided "as is" without warranties of any kind, express or implied. We do not warrant that the Platform will be error-free, uninterrupted, or that information provided is accurate, complete, or current. Reference pricing is indicative only and not suitable for real trading decisions.</p>
        </Section>

        <Section title="9. Limitation of Liability">
          <p>To the maximum extent permitted by applicable law, CarbonX shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Platform, including but not limited to any financial losses from real-world decisions made based on Platform content.</p>
        </Section>

        <Section title="10. Governing Law">
          <p>These Terms are governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts in New Delhi, India.</p>
        </Section>

        <Section title="11. Changes to Terms">
          <p>We may update these Terms from time to time. Continued use of the Platform after changes constitutes acceptance of the new Terms. We will notify registered users of material changes via email or Platform notification.</p>
        </Section>

        <Section title="12. Contact">
          <p>For questions about these Terms, contact us at <span style={{ color: 'var(--neon-cyan)' }}>legal@carbonx.in</span></p>
        </Section>
      </div>

      <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
        <Link to="/privacy" className="btn btn-secondary">Privacy Policy</Link>
        <Link to="/" className="btn btn-secondary">Back to Home</Link>
      </div>
    </div>
  );
}
